import * as React from "react";
import { useState, useEffect } from "react";
import { Popper } from "@mui/base/Popper";
import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import Autocomplete from "@mui/joy/Autocomplete";
import AutocompleteListbox from "@mui/joy/AutocompleteListbox";
import AutocompleteOption from "@mui/joy/AutocompleteOption";
import Box from "@mui/joy/Box";
import Link from "@mui/joy/Link";
import Typography from "@mui/joy/Typography";
import Sheet from "@mui/joy/Sheet";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import colors from "@mui/joy/colors";
import theme from "@theme";
import { Avatar } from "@InnoUI";
import SC from "./style";

const Listbox = (props) => (
  <AutocompleteListbox
    {...props}
    variant="plain"
    size="sm"
    sx={{
      "--List-padding": "0px",
      "--List-radius": "0px",
      "--ListItem-paddingX": "8px",
      "--ListItem-paddingY": "8px",
      minWidth: "100%",
    }}
  />
);

const SingleSearchableDropdown = (props) => {
  const {
    name = "Select",
    labels = [],
    onChange = () => {},
    selectedValue = [],
    icon: Icon,
    avatarComponent: AvatarComponent,
    height,
    width,
    sx,
    disabled = false,
    style,
  } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const [value, setValue] = useState(selectedValue);
  const [pendingValue, setPendingValue] = useState(null);

  const handleClick = (event) => {
    event.preventDefault();
    if (!disabled) {
      setPendingValue(value);
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  };

  const handleSelectionChange = (event, newValue) => {
    setPendingValue(newValue);
    setValue(newValue);
    handleClose();
    if (!areObjectsEqual(newValue, value)) {
      onChange(newValue, name);
    }
  };

  const areObjectsEqual = (obj1, obj2) => {
    if (
      typeof obj1 !== "object" ||
      obj1 === null ||
      typeof obj2 !== "object" ||
      obj2 === null
    ) {
      return false;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (let key of keys1) {
      if (obj1[key] !== obj2[key]) {
        return false;
      }
    }

    return true;
  };

  useEffect(() => {
    setValue(selectedValue);
  }, []);

  const open = Boolean(anchorEl);
  const id = open ? "zenric-label" : undefined;

  return (
    <>
      <SC.GlobalStyles width={width} height={height} />
      <Box>
        <Link
          color="neutral"
          component="button"
          underline="none"
          level="body-xs"
          aria-describedby={id}
          onClick={handleClick}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "fit-Content",
            fontWeight: "lg",
            padding: "0px",
            color: "text.secondary",
            py: 1,
            "&:hover": {
              color: "primary.plainColor",
            },
          }}
        >
          {Icon}
        </Link>
      </Box>
      <Popper
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ ...style, zIndex: 9000 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Sheet
            variant="outlined"
            sx={(theme) => ({
              width: 300,
              boxShadow: "md",
              borderRadius: "6px",
              overflow: "hidden",
              "--joy-palette-neutral-plainBg": "#fff",
              "--joy-palette-background-surface": "#fff",
              [theme.getColorSchemeSelector("dark")]: {
                "--joy-palette-neutral-plainBg": "#000",
                "--joy-palette-background-surface": "#000",
              },
            })}
          >
            <Autocomplete
              open
              autoFocus
              size="sm"
              placeholder="Select"
              slots={{ listbox: Listbox }}
              onClose={(event, reason) => {
                if (reason === "escape") {
                  handleClose();
                }
              }}
              onChange={handleSelectionChange}
              value={pendingValue}
              disableClearable
              disableCloseOnSelect
              forcePopupIcon={false}
              /*  renderTags={() => null} */
              noOptionsText="No labels"
              renderOption={(props, option, { selected }) => (
                <AutocompleteOption
                  {...props}
                  color="neutral"
                  sx={(theme) => ({
                    alignItems: "flex-start",
                    border: "none",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "--joy-palette-neutral-plainHoverBg": "rgba(0, 0, 0, 0.03)",
                    "--joy-palette-neutral-plainActiveBg":
                      "rgba(0, 0, 0, 0.03)",
                    [theme.getColorSchemeSelector("dark")]: {
                      "--joy-palette-neutral-plainHoverBg": colors.grey[800],
                      "--joy-palette-neutral-plainActiveBg": colors.grey[800],
                    },
                    '&[aria-selected="true"]': {
                      fontWeight: "normal",
                    },
                    "&:first-of-type": {
                      borderTop: "1px solid",
                      borderColor: "divider",
                    },
                  })}
                >
                  <DoneIcon
                    sx={{ visibility: selected ? "visible" : "hidden" }}
                  />
                  {AvatarComponent && (
                    <Avatar.LetterAvatar
                      name={option.name || ""}
                      style={{
                        bgcolor: "blue",
                        width: 30,
                        height: 30,
                        fontSize: "12px",
                        color: theme.colors.white,
                        border: `2px solid ${theme.colors.white}`,
                      }}
                    />
                  )}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography level="title-sm">
                      {option.name || ""}
                    </Typography>
                    <Typography level="body-xs">
                      {option.email || ""}
                    </Typography>
                  </Box>
                  {/* <CloseIcon
                    sx={{ visibility: selected ? "visible" : "hidden" }}
                  /> */}
                </AutocompleteOption>
              )}
              options={labels}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option?.name || ""}
              sx={{
                p: "4px 2px",
                borderTop: "1px solid",
                borderBottom: "1px solid",
                borderColor: "divider",
                "--Input-radius": "4px",
                m: "0.75rem 0.5rem",
              }}
            />
          </Sheet>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default SingleSearchableDropdown;
